import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Camera,
  Upload,
  Receipt,
  Bot,
  Send,
  Euro,
  TrendingUp,
  TrendingDown,
  AlertCircle,
  CheckCircle2,
  Clock,
  FileText,
  Plus,
  Trash2,
  Edit,
  Eye,
  Loader2,
  Sparkles,
  Calculator,
  PiggyBank,
  CreditCard,
  Building2,
  Image as ImageIcon,
  Download,
  BarChart3,
  Scale,
  FileSpreadsheet,
  Wallet,
  Check,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import api from '@/lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Composant pour capturer une photo
const CameraCapture = ({ onCapture, onClose }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const startCamera = async () => {
      try {
        const mediaStream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' }
        });
        setStream(mediaStream);
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
        }
      } catch (err) {
        setError("Impossible d'accéder à la caméra. Veuillez autoriser l'accès.");
        console.error(err);
      }
    };
    startCamera();

    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);
      
      canvas.toBlob((blob) => {
        if (blob) {
          const file = new File([blob], `facture_${Date.now()}.jpg`, { type: 'image/jpeg' });
          onCapture(file);
        }
      }, 'image/jpeg', 0.9);
    }
  };

  if (error) {
    return (
      <div className="text-center py-8">
        <AlertCircle className="w-12 h-12 mx-auto mb-4 text-destructive" />
        <p className="text-muted-foreground">{error}</p>
        <Button onClick={onClose} variant="outline" className="mt-4">
          Fermer
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="relative aspect-[3/4] bg-black rounded-lg overflow-hidden">
        <video
          ref={videoRef}
          autoPlay
          playsInline
          className="w-full h-full object-cover"
        />
        <canvas ref={canvasRef} className="hidden" />
      </div>
      <div className="flex gap-2">
        <Button onClick={onClose} variant="outline" className="flex-1">
          Annuler
        </Button>
        <Button onClick={capturePhoto} className="flex-1 gap-2">
          <Camera className="w-4 h-4" />
          Capturer
        </Button>
      </div>
    </div>
  );
};

// Modale aperçu facture (image fournisseur OU PDF vente)
const InvoicePreviewDialog = ({ open, onClose, title, mode, src }) => {
  // mode: 'image' | 'pdf'
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-5xl h-[90vh] flex flex-col" data-testid="invoice-preview-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Eye className="w-5 h-5" /> {title || 'Aperçu facture'}
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-hidden bg-muted rounded-md">
          {!src ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : mode === 'image' ? (
            <div className="w-full h-full overflow-auto flex items-start justify-center p-2">
              <img src={src} alt={title} className="max-w-full" data-testid="invoice-preview-image" />
            </div>
          ) : (
            <iframe src={src} title={title} className="w-full h-full border-0" data-testid="invoice-preview-iframe" />
          )}
        </div>
        <DialogFooter className="gap-2">
          {mode === 'pdf' && src && (
            <Button asChild variant="outline" data-testid="btn-open-pdf-newtab">
              <a href={src} target="_blank" rel="noopener noreferrer">
                <Download className="w-4 h-4 mr-2" /> Ouvrir / Télécharger
              </a>
            </Button>
          )}
          <Button onClick={onClose} variant="outline" data-testid="btn-close-preview">Fermer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Composant carte de facture
const FactureCard = ({ facture, onEdit, onDelete, onValidate, onView }) => {
  const isValidated = facture.validated;
  const isPaid = facture.statut_paiement === 'payee';
  
  return (
    <div 
      className={cn(
        "p-4 rounded-lg border transition-colors",
        !isValidated && "border-amber-200 bg-amber-50/50 dark:bg-amber-950/20",
        isValidated && "border-border hover:bg-muted/50"
      )}
      data-testid={`facture-${facture.id}`}
    >
      <div className="flex items-start gap-4">
        {facture.image_url ? (
          <div className="w-16 h-16 rounded-lg bg-muted overflow-hidden flex-shrink-0">
            <img 
              src={`${API_URL}${facture.image_url}`} 
              alt="Facture"
              className="w-full h-full object-cover"
            />
          </div>
        ) : (
          <div className="w-16 h-16 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
            <Receipt className="w-8 h-8 text-muted-foreground" />
          </div>
        )}
        
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between">
            <div>
              <p className="font-medium truncate">{facture.fournisseur}</p>
              <p className="text-sm text-muted-foreground">{facture.date_facture}</p>
            </div>
            <div className="text-right">
              <p className="font-bold text-lg">{facture.montant_ttc?.toLocaleString()}€</p>
              <Badge variant={isPaid ? "default" : "outline"} className="text-xs">
                {isPaid ? "Payée" : facture.statut_paiement === 'a_payer' ? "À payer" : "En attente"}
              </Badge>
            </div>
          </div>
          
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <Badge variant="secondary" className="text-xs">
              {facture.categorie}
            </Badge>
            {facture.extraction_ia && !isValidated && (
              <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">
                <Sparkles className="w-3 h-3 mr-1" />
                À valider
              </Badge>
            )}
            {facture.confidence_score && facture.confidence_score < 0.7 && (
              <Badge variant="destructive" className="text-xs">
                Confiance faible
              </Badge>
            )}
          </div>
        </div>
        
        <div className="flex flex-col gap-1">
          <Button size="icon" variant="ghost" onClick={() => onView ? onView(facture) : onEdit(facture)} title="Voir / Visualiser" data-testid={`btn-voir-facture-${facture.id}`}>
            <Eye className="w-4 h-4" />
          </Button>
          <Button size="icon" variant="ghost" onClick={() => onEdit(facture)} title="Modifier">
            <Edit className="w-4 h-4" />
          </Button>
          {!isValidated && (
            <Button size="icon" variant="ghost" onClick={() => onValidate(facture.id)} className="text-green-600" title="Valider">
              <CheckCircle2 className="w-4 h-4" />
            </Button>
          )}
          <Button size="icon" variant="ghost" onClick={() => onDelete(facture.id)} className="text-destructive" title="Supprimer">
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

// Composant Assistant IA
const AssistantChat = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const scrollRef = useRef(null);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    
    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);
    
    try {
      const response = await api.post('/comptabilite/assistant/chat', {
        message: userMessage,
        conversation_id: conversationId
      });
      
      setConversationId(response.data.conversation_id);
      setMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
    } catch (error) {
      toast.error("Erreur de communication avec l'assistant");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const suggestions = [
    "Quel est le total de mes dépenses ce mois ?",
    "Quelles sont mes plus grosses dépenses ?",
    "Ai-je des factures à payer bientôt ?",
    "Comment optimiser ma TVA déductible ?"
  ];

  return (
    <Card className="h-[600px] flex flex-col">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2">
          <Bot className="w-5 h-5 text-primary" />
          Assistant Comptable IA
        </CardTitle>
        <CardDescription>
          Posez vos questions sur vos finances
        </CardDescription>
      </CardHeader>
      
      <CardContent className="flex-1 flex flex-col overflow-hidden">
        <ScrollArea className="flex-1 pr-4" ref={scrollRef}>
          <div className="space-y-4">
            {messages.length === 0 && (
              <div className="text-center py-8">
                <Bot className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground mb-4">
                  Comment puis-je vous aider avec votre comptabilité ?
                </p>
                <div className="flex flex-wrap gap-2 justify-center">
                  {suggestions.map((suggestion, idx) => (
                    <Button
                      key={idx}
                      variant="outline"
                      size="sm"
                      onClick={() => setInput(suggestion)}
                      className="text-xs"
                    >
                      {suggestion}
                    </Button>
                  ))}
                </div>
              </div>
            )}
            
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={cn(
                  "flex",
                  msg.role === 'user' ? "justify-end" : "justify-start"
                )}
              >
                <div
                  className={cn(
                    "max-w-[80%] rounded-lg px-4 py-2",
                    msg.role === 'user'
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted"
                  )}
                >
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                </div>
              </div>
            ))}
            
            {loading && (
              <div className="flex justify-start">
                <div className="bg-muted rounded-lg px-4 py-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                </div>
              </div>
            )}
          </div>
        </ScrollArea>
        
        <div className="flex gap-2 mt-4 pt-4 border-t">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
            placeholder="Posez votre question..."
            disabled={loading}
            data-testid="assistant-input"
          />
          <Button onClick={sendMessage} disabled={loading || !input.trim()}>
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Composant Facturation Électronique
const FacturationElectronique = () => {
  const [invoices, setInvoices] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [clients, setClients] = useState([]); // CRM entreprises + contacts fusionnés
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [documentType, setDocumentType] = useState('invoice'); // 'invoice' ou 'quote'
  const [selectedClient, setSelectedClient] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [sireneSuggestions, setSireneSuggestions] = useState([]);
  const [sireneLoading, setSireneLoading] = useState(false);
  const [items, setItems] = useState([{ description: '', quantity: 1, unit_price: 0 }]);
  const [notes, setNotes] = useState('');
  const [creating, setCreating] = useState(false);
  const [societeSettings, setSocieteSettings] = useState({
    logo_url: '',
    cgv: '',
    siret: '',
    tva_intra: '',
    iban: '',
    bic: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [invoicesRes, quotesRes, contactsRes, entreprisesRes] = await Promise.all([
        api.get('/billing/invoices'),
        api.get('/billing/quotes'),
        api.get('/crm/contacts?type_contact=client').catch(() => ({ data: { contacts: [] } })),
        api.get('/crm/entreprises?limit=500').catch(() => ({ data: { entreprises: [] } }))
      ]);
      setInvoices(invoicesRes.data.invoices || []);
      setQuotes(quotesRes.data.quotes || []);

      // Fusionner contacts + entreprises CRM (les entreprises sont prioritaires car ce sont des clients réels)
      const contacts = (contactsRes.data.contacts || []).map(c => ({
        id: c.id,
        nom: c.nom || '',
        prenom: c.prenom || '',
        display_name: `${c.nom || ''} ${c.prenom || ''}`.trim(),
        type: 'contact',
      }));
      const entreprises = (entreprisesRes.data.entreprises || []).map(e => ({
        id: e.id,
        nom: e.nom || e.raison_sociale || '',
        prenom: '',
        display_name: e.nom || e.raison_sociale || '',
        ville: e.ville || '',
        type: 'entreprise',
      }));
      setClients([...entreprises, ...contacts].filter(c => c.display_name));
    } catch (error) {
      console.error('Erreur chargement:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filtrer les clients selon la saisie (3+ caractères pour matching, sinon affiche les 10 premiers)
  const filteredClients = clientSearch.trim().length >= 1
    ? clients.filter(c => c.display_name.toLowerCase().includes(clientSearch.toLowerCase().trim())).slice(0, 12)
    : clients.slice(0, 10);

  const exactMatch = clients.find(c =>
    c.display_name.toLowerCase().trim() === clientSearch.toLowerCase().trim()
  );

  // Recherche Sirene (API gouv) en debounce 400ms si pas de match local
  useEffect(() => {
    const q = clientSearch.trim();
    if (q.length < 3 || filteredClients.length >= 5) {
      setSireneSuggestions([]);
      return;
    }
    const timer = setTimeout(async () => {
      setSireneLoading(true);
      try {
        const r = await api.get(`/sirene/search?q=${encodeURIComponent(q)}&limit=5`);
        // Filtrer les résultats déjà dans le CRM (par SIRET ou nom)
        const newOnes = (r.data.results || []).filter(s => {
          const exists = clients.some(c =>
            (c.siret && c.siret === s.siret) ||
            c.display_name.toLowerCase() === (s.raison_sociale || s.nom_complet || '').toLowerCase()
          );
          return !exists;
        });
        setSireneSuggestions(newOnes);
      } catch (e) {
        setSireneSuggestions([]);
      } finally {
        setSireneLoading(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [clientSearch, filteredClients.length, clients]);

  // Importer une entreprise depuis Sirene → CRM puis sélectionner
  const importFromSirene = async (entreprise) => {
    try {
      const response = await api.post('/crm/entreprises', {
        nom: entreprise.raison_sociale || entreprise.nom_complet,
        siret: entreprise.siret,
        siren: entreprise.siren,
        tva_intra: entreprise.tva_intra,
        adresse: entreprise.adresse,
        code_postal: entreprise.code_postal,
        ville: entreprise.ville,
        forme_juridique: entreprise.forme_juridique,
        naf_code: entreprise.naf_code,
        naf_libelle: entreprise.naf_libelle,
        dirigeant_nom: entreprise.dirigeant_nom,
        statut: 'client',
        source: 'sirene_api',
      });
      const e = response.data.entreprise;
      const newClient = {
        id: e.id,
        nom: e.nom,
        prenom: '',
        display_name: e.nom,
        ville: e.ville || entreprise.ville,
        siret: e.siret || entreprise.siret,
        type: 'entreprise',
      };
      setClients(prev => [newClient, ...prev]);
      setSelectedClient(newClient.id);
      setClientSearch(newClient.display_name);
      setShowSuggestions(false);
      setSireneSuggestions([]);
      toast.success(`${e.nom} importé depuis Sirene + ajouté au CRM`);
    } catch (error) {
      toast.error('Erreur import Sirene');
      console.error(error);
    }
  };

  // Créer un nouveau client en CRM puis le sélectionner
  const createNewClientInCRM = async () => {
    const nom = clientSearch.trim();
    if (!nom || nom.length < 2) {
      toast.error('Nom client trop court');
      return;
    }
    try {
      const response = await api.post('/crm/entreprises', {
        nom: nom,
        statut: 'client',
        source: 'facturation',
      });
      const newEntreprise = response.data.entreprise;
      const newClient = {
        id: newEntreprise.id,
        nom: newEntreprise.nom,
        prenom: '',
        display_name: newEntreprise.nom,
        type: 'entreprise',
      };
      setClients(prev => [newClient, ...prev]);
      setSelectedClient(newClient.id);
      setClientSearch(newClient.display_name);
      setShowSuggestions(false);
      toast.success(`Client "${nom}" créé dans le CRM`);
    } catch (error) {
      toast.error('Erreur création client CRM');
      console.error(error);
    }
  };

  const addItem = () => {
    setItems([...items, { description: '', quantity: 1, unit_price: 0 }]);
  };

  const removeItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const updateItem = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = field === 'quantity' || field === 'unit_price' ? parseFloat(value) || 0 : value;
    setItems(newItems);
  };

  const calculateTotal = () => {
    const subtotal = items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
    const tva = subtotal * 0.2;
    return { subtotal, tva, total: subtotal + tva };
  };

  const handleCreate = async () => {
    if (!selectedClient) {
      toast.error('Veuillez sélectionner un client');
      return;
    }
    if (items.some(item => !item.description || item.unit_price <= 0)) {
      toast.error('Veuillez remplir tous les articles');
      return;
    }

    setCreating(true);
    try {
      const endpoint = documentType === 'invoice' ? '/billing/invoices' : '/billing/quotes';
      const response = await api.post(endpoint, {
        client_id: selectedClient,
        items: items,
        notes: notes
      });

      if (response.data.success) {
        toast.success(documentType === 'invoice' ? 'Facture créée !' : 'Devis créé !');
        setShowCreateDialog(false);
        resetForm();
        loadData();
      }
    } catch (error) {
      toast.error('Erreur lors de la création');
      console.error(error);
    } finally {
      setCreating(false);
    }
  };

  const resetForm = () => {
    setSelectedClient('');
    setClientSearch('');
    setShowSuggestions(false);
    setItems([{ description: '', quantity: 1, unit_price: 0 }]);
    setNotes('');
    setDocumentType('invoice');
  };

  const handleDownloadPDF = async (invoiceId) => {
    try {
      const response = await api.get(`/billing/invoices/${invoiceId}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `facture_${invoiceId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Erreur téléchargement PDF');
    }
  };

  const [previewInvoice, setPreviewInvoice] = useState({ open: false, src: null, title: '' });

  const handlePreviewInvoice = async (invoice) => {
    setPreviewInvoice({ open: true, src: null, title: invoice.invoice_number || 'Facture' });
    try {
      const response = await api.get(`/billing/invoices/${invoice.id}/pdf`, {
        responseType: 'blob'
      });
      const blobUrl = window.URL.createObjectURL(
        new Blob([response.data], { type: 'application/pdf' })
      );
      setPreviewInvoice({ open: true, src: blobUrl, title: invoice.invoice_number || 'Facture' });
    } catch (error) {
      toast.error('Impossible de charger le PDF');
      setPreviewInvoice({ open: false, src: null, title: '' });
    }
  };

  const handleSendInvoice = async (invoiceId) => {
    try {
      await api.post(`/billing/invoices/${invoiceId}/send`);
      toast.success('Facture envoyée !');
      loadData();
    } catch (error) {
      toast.error("Erreur lors de l'envoi");
    }
  };

  const handleConvertToInvoice = async (quoteId) => {
    try {
      await api.post(`/billing/quotes/${quoteId}/convert-to-invoice`);
      toast.success('Devis converti en facture !');
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la conversion');
    }
  };

  const totals = calculateTotal();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Facturation Électronique</h3>
          <p className="text-sm text-muted-foreground">Gérez vos devis et factures</p>
        </div>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="btn-create-invoice">
              <Plus className="w-4 h-4" />
              Nouveau document
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Créer un document</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Type de document */}
              <div className="flex gap-4">
                <Button
                  variant={documentType === 'quote' ? 'default' : 'outline'}
                  onClick={() => setDocumentType('quote')}
                  className="flex-1"
                >
                  Devis
                </Button>
                <Button
                  variant={documentType === 'invoice' ? 'default' : 'outline'}
                  onClick={() => setDocumentType('invoice')}
                  className="flex-1"
                >
                  Facture
                </Button>
              </div>

              {/* Sélection client - Autocomplete avec création nouveau client */}
              <div className="space-y-2 relative">
                <Label>Client *</Label>
                <Input
                  placeholder="Tapez 3 lettres du client (ex: 'mar' pour Marchand)..."
                  value={clientSearch}
                  onChange={(e) => {
                    setClientSearch(e.target.value);
                    setShowSuggestions(true);
                    if (selectedClient && !clients.find(c => c.id === selectedClient && c.display_name === e.target.value)) {
                      setSelectedClient('');
                    }
                  }}
                  onFocus={() => setShowSuggestions(true)}
                  data-testid="input-client-search"
                />
                {showSuggestions && (filteredClients.length > 0 || clientSearch.trim().length >= 2 || sireneSuggestions.length > 0) && (
                  <div className="absolute z-50 w-full max-h-72 overflow-y-auto bg-popover border rounded-md shadow-lg mt-1">
                    {filteredClients.map(client => (
                      <button
                        key={client.id}
                        type="button"
                        onClick={() => {
                          setSelectedClient(client.id);
                          setClientSearch(client.display_name);
                          setShowSuggestions(false);
                        }}
                        className={`w-full text-left px-3 py-2 hover:bg-muted text-sm flex items-center justify-between ${
                          selectedClient === client.id ? 'bg-primary/10' : ''
                        }`}
                        data-testid={`suggestion-client-${client.id}`}
                      >
                        <span>
                          {client.display_name}
                          {client.ville && <span className="text-muted-foreground ml-2 text-xs">({client.ville})</span>}
                        </span>
                        <Badge variant="outline" className="text-[10px]">
                          {client.type === 'entreprise' ? 'CRM' : 'Contact'}
                        </Badge>
                      </button>
                    ))}

                    {/* Suggestions Sirene API gouv */}
                    {sireneSuggestions.length > 0 && (
                      <>
                        <div className="px-3 py-1.5 bg-blue-50 text-xs font-semibold text-blue-700 border-t border-b">
                          🔍 Suggestions API Entreprises (annuaire INSEE)
                        </div>
                        {sireneSuggestions.map(s => (
                          <button
                            key={s.siret}
                            type="button"
                            onClick={() => importFromSirene(s)}
                            className="w-full text-left px-3 py-2 hover:bg-blue-50 text-sm border-b last:border-0"
                            data-testid={`suggestion-sirene-${s.siret}`}
                          >
                            <div className="flex items-center justify-between">
                              <span className="font-medium">{s.raison_sociale || s.nom_complet}</span>
                              <Badge variant="default" className="text-[10px] bg-blue-600">+ Importer</Badge>
                            </div>
                            <div className="text-xs text-muted-foreground mt-0.5 space-y-0.5">
                              <div>📋 SIRET: {s.siret} {s.naf_libelle && `· ${s.naf_libelle}`}</div>
                              {s.adresse && <div>📍 {s.adresse}</div>}
                              {s.dirigeant_nom && <div>👤 Dirigeant: {s.dirigeant_nom}</div>}
                            </div>
                          </button>
                        ))}
                      </>
                    )}

                    {sireneLoading && (
                      <div className="px-3 py-2 text-xs text-muted-foreground italic">
                        Recherche annuaire INSEE en cours...
                      </div>
                    )}

                    {clientSearch.trim().length >= 2 && !exactMatch && (
                      <button
                        type="button"
                        onClick={createNewClientInCRM}
                        className="w-full text-left px-3 py-2 hover:bg-primary/10 border-t text-sm flex items-center gap-2 text-primary font-medium"
                        data-testid="btn-create-new-client"
                      >
                        <Plus className="w-4 h-4" />
                        Créer "{clientSearch.trim()}" comme nouveau client (sans SIRET)
                      </button>
                    )}
                    {filteredClients.length === 0 && sireneSuggestions.length === 0 && clientSearch.trim().length < 2 && (
                      <div className="px-3 py-2 text-sm text-muted-foreground">
                        Tapez au moins 2 lettres pour chercher (CRM + annuaire INSEE)
                      </div>
                    )}
                  </div>
                )}
                {selectedClient && (
                  <p className="text-xs text-green-600 flex items-center gap-1">
                    <Check className="w-3 h-3" /> Client sélectionné depuis le CRM
                  </p>
                )}
              </div>

              {/* Articles */}
              <div className="space-y-2">
                <Label>Articles</Label>
                <div className="space-y-2">
                  {items.map((item, index) => (
                    <div key={index} className="grid grid-cols-12 gap-2 items-center">
                      <Input
                        className="col-span-5"
                        placeholder="Description"
                        value={item.description}
                        onChange={(e) => updateItem(index, 'description', e.target.value)}
                      />
                      <Input
                        className="col-span-2"
                        type="number"
                        placeholder="Qté"
                        value={item.quantity}
                        onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                      />
                      <Input
                        className="col-span-3"
                        type="number"
                        step="0.01"
                        placeholder="Prix HT"
                        value={item.unit_price}
                        onChange={(e) => updateItem(index, 'unit_price', e.target.value)}
                      />
                      <div className="col-span-1 text-right font-medium">
                        {(item.quantity * item.unit_price).toFixed(2)}€
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="col-span-1"
                        onClick={() => removeItem(index)}
                        disabled={items.length === 1}
                      >
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  ))}
                </div>
                <Button variant="outline" size="sm" onClick={addItem} className="mt-2">
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter une ligne
                </Button>
              </div>

              {/* Totaux */}
              <Card className="bg-muted/50">
                <CardContent className="p-4 space-y-1">
                  <div className="flex justify-between text-sm">
                    <span>Sous-total HT</span>
                    <span>{totals.subtotal.toFixed(2)} €</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>TVA (20%)</span>
                    <span>{totals.tva.toFixed(2)} €</span>
                  </div>
                  <div className="flex justify-between font-bold text-lg pt-2 border-t">
                    <span>Total TTC</span>
                    <span>{totals.total.toFixed(2)} €</span>
                  </div>
                </CardContent>
              </Card>

              {/* Notes */}
              <div className="space-y-2">
                <Label>Notes / Conditions</Label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Conditions de paiement, remarques..."
                  rows={2}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                Annuler
              </Button>
              <Button onClick={handleCreate} disabled={creating}>
                {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Créer {documentType === 'invoice' ? 'la facture' : 'le devis'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats rapides */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <FileText className="w-6 h-6 mx-auto text-blue-500 mb-1" />
            <p className="text-2xl font-bold">{quotes.length}</p>
            <p className="text-xs text-muted-foreground">Devis</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <Receipt className="w-6 h-6 mx-auto text-green-500 mb-1" />
            <p className="text-2xl font-bold">{invoices.length}</p>
            <p className="text-xs text-muted-foreground">Factures</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <Clock className="w-6 h-6 mx-auto text-amber-500 mb-1" />
            <p className="text-2xl font-bold">
              {invoices.filter(i => i.status === 'sent').length}
            </p>
            <p className="text-xs text-muted-foreground">En attente</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <Euro className="w-6 h-6 mx-auto text-primary mb-1" />
            <p className="text-2xl font-bold">
              {invoices.reduce((sum, i) => sum + (i.total || 0), 0).toLocaleString()}€
            </p>
            <p className="text-xs text-muted-foreground">Total facturé</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs Devis / Factures */}
      <Tabs defaultValue="invoices">
        <TabsList>
          <TabsTrigger value="quotes">
            <FileText className="w-4 h-4 mr-2" />
            Devis ({quotes.length})
          </TabsTrigger>
          <TabsTrigger value="invoices">
            <Receipt className="w-4 h-4 mr-2" />
            Factures ({invoices.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="quotes" className="mt-4">
          {quotes.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center">
                <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground">Aucun devis</p>
                <Button className="mt-4" onClick={() => { setDocumentType('quote'); setShowCreateDialog(true); }}>
                  <Plus className="w-4 h-4 mr-2" />
                  Créer un devis
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {quotes.map(quote => (
                <Card key={quote.id} className="hover:bg-muted/50 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{quote.quote_number}</p>
                        <p className="text-sm text-muted-foreground">
                          {quote.client?.nom || 'Client'} • {quote.issue_date?.split('T')[0]}
                        </p>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="font-bold text-lg">{quote.total?.toFixed(2)}€</p>
                          <Badge variant={quote.status === 'converted' ? 'default' : 'outline'}>
                            {quote.status === 'converted' ? 'Converti' : 'En cours'}
                          </Badge>
                        </div>
                        {quote.status !== 'converted' && (
                          <Button size="sm" onClick={() => handleConvertToInvoice(quote.id)}>
                            Convertir
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="invoices" className="mt-4">
          {invoices.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center">
                <Receipt className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground">Aucune facture</p>
                <Button className="mt-4" onClick={() => { setDocumentType('invoice'); setShowCreateDialog(true); }}>
                  <Plus className="w-4 h-4 mr-2" />
                  Créer une facture
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {invoices.map(invoice => (
                <Card key={invoice.id} className="hover:bg-muted/50 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{invoice.invoice_number}</p>
                        <p className="text-sm text-muted-foreground">
                          {invoice.client?.nom || 'Client'} • {invoice.issue_date?.split('T')[0]}
                        </p>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="font-bold text-lg">{invoice.total?.toFixed(2)}€</p>
                          <Badge variant={invoice.status === 'sent' ? 'secondary' : invoice.status === 'paid' ? 'default' : 'outline'}>
                            {invoice.status === 'sent' ? 'Envoyée' : invoice.status === 'paid' ? 'Payée' : 'Brouillon'}
                          </Badge>
                        </div>
                        <div className="flex gap-1">
                          <Button size="icon" variant="ghost" onClick={() => handlePreviewInvoice(invoice)} title="Visualiser" data-testid={`btn-preview-invoice-${invoice.id}`}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button size="icon" variant="ghost" onClick={() => handleDownloadPDF(invoice.id)} title="Télécharger PDF">
                            <FileText className="w-4 h-4" />
                          </Button>
                          {invoice.status === 'draft' && (
                            <Button size="icon" variant="ghost" onClick={() => handleSendInvoice(invoice.id)}>
                              <Send className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Modale aperçu PDF facture vente */}
      <InvoicePreviewDialog
        open={previewInvoice.open}
        onClose={() => {
          if (previewInvoice.src) {
            try { window.URL.revokeObjectURL(previewInvoice.src); } catch (_) {}
          }
          setPreviewInvoice({ open: false, src: null, title: '' });
        }}
        title={previewInvoice.title}
        mode="pdf"
        src={previewInvoice.src}
      />
    </div>
  );
};


// Page principale
export const ComptabilitePage = () => {
  const [factures, setFactures] = useState([]);
  const [categories, setCategories] = useState({});
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState('factures');
  const [showCamera, setShowCamera] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingFacture, setEditingFacture] = useState(null);
  const [periode, setPeriode] = useState('mois');
  const [bilan, setBilan] = useState(null);
  const [compteResultat, setCompteResultat] = useState(null);
  const [tresorerie, setTresorerie] = useState(null);
  const [exercice, setExercice] = useState(new Date().getFullYear());
  const [loadingBilan, setLoadingBilan] = useState(false);
  const [loadingExport, setLoadingExport] = useState(false);
  // Preview facture fournisseur (image)
  const [previewSupplier, setPreviewSupplier] = useState({ open: false, src: null, title: '' });
  
  const fileInputRef = useRef(null);
  
  // Form state
  const [formData, setFormData] = useState({
    fournisseur: '',
    date_facture: new Date().toISOString().split('T')[0],
    montant_ttc: '',
    taux_tva: '20',
    categorie: 'divers',
    description: '',
    mode_paiement: '',
    statut_paiement: 'a_payer'
  });

  const fetchData = async () => {
    try {
      const [facturesRes, categoriesRes, statsRes] = await Promise.all([
        api.get('/comptabilite/factures'),
        api.get('/comptabilite/categories'),
        api.get(`/comptabilite/stats?periode=${periode}`)
      ]);
      
      setFactures(facturesRes.data.factures || []);
      setCategories(categoriesRes.data.categories || {});
      setStats(statsRes.data);
    } catch (error) {
      console.error('Erreur chargement:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const fetchBilan = async () => {
    setLoadingBilan(true);
    try {
      const [bilanRes, compteRes, tresoRes] = await Promise.all([
        api.get(`/comptabilite/bilan?exercice=${exercice}`),
        api.get(`/comptabilite/compte-resultat?exercice=${exercice}`),
        api.get(`/comptabilite/tresorerie?mois=12`)
      ]);
      setBilan(bilanRes.data);
      setCompteResultat(compteRes.data);
      setTresorerie(tresoRes.data);
    } catch (error) {
      console.error('Erreur chargement bilan:', error);
      toast.error('Erreur lors du chargement du bilan');
    } finally {
      setLoadingBilan(false);
    }
  };

  const handleExportFEC = async () => {
    setLoadingExport(true);
    try {
      const response = await api.get(`/comptabilite/export/fec?exercice=${exercice}`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `FEC${exercice}.txt`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Export FEC téléchargé !');
    } catch (error) {
      console.error('Erreur export FEC:', error);
      toast.error("Erreur lors de l'export FEC");
    } finally {
      setLoadingExport(false);
    }
  };

  const handleExportComptable = async (format = 'csv') => {
    setLoadingExport(true);
    try {
      if (format === 'json') {
        const response = await api.get(`/comptabilite/export/comptable?format=json&exercice=${exercice}`);
        const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `comptabilite_${exercice}.json`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        const response = await api.get(`/comptabilite/export/comptable?format=csv&exercice=${exercice}`, {
          responseType: 'blob'
        });
        const blob = new Blob([response.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `comptabilite_${exercice}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
      toast.success('Export téléchargé !');
    } catch (error) {
      console.error('Erreur export:', error);
      toast.error("Erreur lors de l'export");
    } finally {
      setLoadingExport(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [periode]);

  useEffect(() => {
    if (activeTab === 'bilan') {
      fetchBilan();
    }
  }, [activeTab, exercice]);

  const handleFileUpload = async (file) => {
    if (!file) return;
    
    setUploading(true);
    setShowCamera(false);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post('/comptabilite/factures/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.success) {
        toast.success('Facture analysée avec succès !');
        setFactures(prev => [response.data.facture, ...prev]);
        // Rafraîchir les stats
        const statsRes = await api.get(`/comptabilite/stats?periode=${periode}`);
        setStats(statsRes.data);
      }
    } catch (error) {
      console.error('Erreur upload:', error);
      toast.error("Erreur lors de l'analyse de la facture");
    } finally {
      setUploading(false);
    }
  };

  const handleCreateFacture = async () => {
    try {
      const data = {
        ...formData,
        montant_ttc: parseFloat(formData.montant_ttc),
        taux_tva: parseFloat(formData.taux_tva)
      };
      
      const response = await api.post('/comptabilite/factures', data);
      
      if (response.data.success) {
        toast.success('Facture créée !');
        setFactures(prev => [response.data.facture, ...prev]);
        setShowAddDialog(false);
        resetForm();
        fetchData();
      }
    } catch (error) {
      toast.error('Erreur lors de la création');
    }
  };

  const handleUpdateFacture = async () => {
    if (!editingFacture) return;
    
    try {
      const data = {
        ...formData,
        montant_ttc: parseFloat(formData.montant_ttc),
        taux_tva: parseFloat(formData.taux_tva)
      };
      
      await api.put(`/comptabilite/factures/${editingFacture.id}`, data);
      toast.success('Facture mise à jour !');
      setEditingFacture(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDeleteFacture = async (id) => {
    if (!confirm('Supprimer cette facture ?')) return;
    
    try {
      await api.delete(`/comptabilite/factures/${id}`);
      setFactures(prev => prev.filter(f => f.id !== id));
      toast.success('Facture supprimée');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleValidateFacture = async (id) => {
    try {
      await api.post(`/comptabilite/factures/${id}/valider`);
      setFactures(prev => prev.map(f => f.id === id ? {...f, validated: true} : f));
      toast.success('Facture validée');
    } catch (error) {
      toast.error('Erreur lors de la validation');
    }
  };

  const resetForm = () => {
    setFormData({
      fournisseur: '',
      date_facture: new Date().toISOString().split('T')[0],
      montant_ttc: '',
      taux_tva: '20',
      categorie: 'divers',
      description: '',
      mode_paiement: '',
      statut_paiement: 'a_payer'
    });
  };

  const openEditDialog = (facture) => {
    setEditingFacture(facture);
    setFormData({
      fournisseur: facture.fournisseur || '',
      date_facture: facture.date_facture || '',
      montant_ttc: facture.montant_ttc?.toString() || '',
      taux_tva: facture.taux_tva?.toString() || '20',
      categorie: facture.categorie || 'divers',
      description: facture.description || '',
      mode_paiement: facture.mode_paiement || '',
      statut_paiement: facture.statut_paiement || 'a_payer'
    });
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 skeleton" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-32 skeleton rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="comptabilite-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Comptabilité</h1>
          <p className="text-muted-foreground mt-1">
            Gestion des factures et dépenses avec assistant IA
          </p>
        </div>
        
        <div className="flex gap-2">
          <Select value={periode} onValueChange={setPeriode}>
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="mois">Ce mois</SelectItem>
              <SelectItem value="trimestre">Trimestre</SelectItem>
              <SelectItem value="annee">Année</SelectItem>
            </SelectContent>
          </Select>
          
          <Dialog open={showCamera} onOpenChange={setShowCamera}>
            <DialogTrigger asChild>
              <Button variant="outline" className="gap-2" data-testid="btn-camera">
                <Camera className="w-4 h-4" />
                <span className="hidden sm:inline">Photo</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Photographier une facture</DialogTitle>
              </DialogHeader>
              <CameraCapture 
                onCapture={handleFileUpload}
                onClose={() => setShowCamera(false)}
              />
            </DialogContent>
          </Dialog>
          
          <input
            type="file"
            ref={fileInputRef}
            accept="image/*,application/pdf,.pdf,.png,.jpg,.jpeg,.webp,.heic"
            className="hidden"
            onChange={(e) => e.target.files?.[0] && handleFileUpload(e.target.files[0])}
            data-testid="input-file-import"
          />
          
          <Button 
            variant="outline" 
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="gap-2"
            data-testid="btn-upload"
          >
            {uploading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Upload className="w-4 h-4" />
            )}
            <span className="hidden sm:inline">Importer</span>
          </Button>
          
          <Dialog open={showAddDialog || !!editingFacture} onOpenChange={(open) => {
            if (!open) {
              setShowAddDialog(false);
              setEditingFacture(null);
              resetForm();
            }
          }}>
            <DialogTrigger asChild>
              <Button className="gap-2" onClick={() => setShowAddDialog(true)} data-testid="btn-add-facture">
                <Plus className="w-4 h-4" />
                <span className="hidden sm:inline">Ajouter</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle>
                  {editingFacture ? 'Modifier la facture' : 'Nouvelle facture'}
                </DialogTitle>
              </DialogHeader>
              
              <div className="grid gap-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Fournisseur</Label>
                    <Input
                      value={formData.fournisseur}
                      onChange={(e) => setFormData({...formData, fournisseur: e.target.value})}
                      placeholder="Nom du fournisseur"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Date</Label>
                    <Input
                      type="date"
                      value={formData.date_facture}
                      onChange={(e) => setFormData({...formData, date_facture: e.target.value})}
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Montant TTC (€)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.montant_ttc}
                      onChange={(e) => setFormData({...formData, montant_ttc: e.target.value})}
                      placeholder="0.00"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Taux TVA (%)</Label>
                    <Select 
                      value={formData.taux_tva} 
                      onValueChange={(v) => setFormData({...formData, taux_tva: v})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="20">20%</SelectItem>
                        <SelectItem value="10">10%</SelectItem>
                        <SelectItem value="5.5">5.5%</SelectItem>
                        <SelectItem value="2.1">2.1%</SelectItem>
                        <SelectItem value="0">0%</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Catégorie</Label>
                    <Select 
                      value={formData.categorie} 
                      onValueChange={(v) => setFormData({...formData, categorie: v})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(categories).map(([key, cat]) => (
                          <SelectItem key={key} value={key}>
                            {cat.nom}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Statut</Label>
                    <Select 
                      value={formData.statut_paiement} 
                      onValueChange={(v) => setFormData({...formData, statut_paiement: v})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="a_payer">À payer</SelectItem>
                        <SelectItem value="payee">Payée</SelectItem>
                        <SelectItem value="en_attente">En attente</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Description optionnelle..."
                    rows={2}
                  />
                </div>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => {
                  setShowAddDialog(false);
                  setEditingFacture(null);
                  resetForm();
                }}>
                  Annuler
                </Button>
                <Button onClick={editingFacture ? handleUpdateFacture : handleCreateFacture}>
                  {editingFacture ? 'Mettre à jour' : 'Créer'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* KPI Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="card-hover">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Dépenses</span>
                <TrendingDown className="w-5 h-5 text-red-500" />
              </div>
              <p className="text-2xl font-bold text-red-600">
                {stats.totaux?.depenses?.toLocaleString() || 0}€
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {stats.nb_factures || 0} factures
              </p>
            </CardContent>
          </Card>
          
          <Card className="card-hover">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Recettes</span>
                <TrendingUp className="w-5 h-5 text-green-500" />
              </div>
              <p className="text-2xl font-bold text-green-600">
                {stats.totaux?.recettes?.toLocaleString() || 0}€
              </p>
            </CardContent>
          </Card>
          
          <Card className="card-hover">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">À payer</span>
                <Clock className="w-5 h-5 text-amber-500" />
              </div>
              <p className="text-2xl font-bold text-amber-600">
                {stats.a_payer?.total?.toLocaleString() || 0}€
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {stats.a_payer?.count || 0} factures
              </p>
            </CardContent>
          </Card>
          
          <Card className="card-hover">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">TVA déductible</span>
                <Calculator className="w-5 h-5 text-primary" />
              </div>
              <p className="text-2xl font-bold text-primary">
                {stats.totaux?.tva_deductible?.toLocaleString() || 0}€
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Alert for unvalidated invoices */}
      {stats?.a_valider > 0 && (
        <Card className="border-amber-200 bg-amber-50/50 dark:bg-amber-950/20">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <Sparkles className="w-5 h-5 text-amber-500" />
              <div>
                <p className="font-medium">
                  {stats.a_valider} facture{stats.a_valider > 1 ? 's' : ''} à valider
                </p>
                <p className="text-sm text-muted-foreground">
                  Des factures ont été extraites par l'IA et nécessitent votre validation
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex-wrap h-auto gap-1">
          <TabsTrigger value="factures" data-testid="tab-factures" className="gap-2">
            <Receipt className="w-4 h-4" />
            <span className="hidden sm:inline">Factures Fournisseurs</span>
            <span className="sm:hidden">Fournisseurs</span>
          </TabsTrigger>
          <TabsTrigger value="facturation" data-testid="tab-facturation" className="gap-2">
            <FileText className="w-4 h-4" />
            <span className="hidden sm:inline">Facturation Client</span>
            <span className="sm:hidden">Clients</span>
          </TabsTrigger>
          <TabsTrigger value="bilan" data-testid="tab-bilan" className="gap-2">
            <Scale className="w-4 h-4" />
            <span className="hidden sm:inline">Bilan & Résultat</span>
            <span className="sm:hidden">Bilan</span>
          </TabsTrigger>
          <TabsTrigger value="exports" data-testid="tab-exports" className="gap-2">
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">Exports Comptables</span>
            <span className="sm:hidden">Exports</span>
          </TabsTrigger>
          <TabsTrigger value="assistant" data-testid="tab-assistant" className="gap-2">
            <Bot className="w-4 h-4" />
            <span className="hidden sm:inline">Assistant IA</span>
            <span className="sm:hidden">IA</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="factures" className="space-y-4 mt-4">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h3 className="font-semibold text-lg">Factures Fournisseurs & Tickets</h3>
              <p className="text-sm text-muted-foreground">Ce que vous payez : factures reçues, tickets, notes de frais</p>
            </div>
          </div>
          {factures.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Receipt className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <h3 className="font-medium mb-2">Aucune facture fournisseur</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Photographiez ou importez vos factures fournisseurs et tickets
                </p>
                <div className="flex gap-2 justify-center">
                  <Button variant="outline" onClick={() => setShowCamera(true)}>
                    <Camera className="w-4 h-4 mr-2" />
                    Photographier
                  </Button>
                  <Button onClick={() => setShowAddDialog(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Saisir manuellement
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {factures.map((facture) => (
                <FactureCard
                  key={facture.id}
                  facture={facture}
                  onEdit={openEditDialog}
                  onDelete={handleDeleteFacture}
                  onValidate={handleValidateFacture}
                  onView={(f) => setPreviewSupplier({
                    open: true,
                    src: f.image_url ? `${API_URL}${f.image_url}` : null,
                    title: `${f.fournisseur || 'Facture'} — ${f.date_facture || ''}`,
                    mode: 'image',
                  })}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="assistant" className="mt-4">
          <AssistantChat />
        </TabsContent>

        <TabsContent value="facturation" className="mt-4">
          <FacturationElectronique />
        </TabsContent>

        {/* Onglet Bilan & Compte de Résultat */}
        <TabsContent value="bilan" className="mt-4 space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-lg">Bilan & Compte de Résultat</h3>
              <p className="text-sm text-muted-foreground">Vue en temps réel de votre situation comptable</p>
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-sm">Exercice:</Label>
              <Select value={String(exercice)} onValueChange={(v) => setExercice(parseInt(v))}>
                <SelectTrigger className="w-[100px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[2024, 2025, 2026].map(year => (
                    <SelectItem key={year} value={String(year)}>{year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={fetchBilan} disabled={loadingBilan}>
                {loadingBilan ? <Loader2 className="w-4 h-4 animate-spin" /> : "Actualiser"}
              </Button>
            </div>
          </div>

          {loadingBilan ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : bilan ? (
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Bilan */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Scale className="w-5 h-5" />
                    Bilan - Exercice {exercice}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Actif */}
                  <div className="border rounded-lg p-4 bg-green-50/30 dark:bg-green-950/20">
                    <h4 className="font-semibold text-green-700 dark:text-green-400 mb-3">ACTIF</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>Immobilisations</span>
                        <span className="font-medium">{bilan.actif?.immobilisations?.total?.toLocaleString() || 0}€</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Stocks</span>
                        <span className="font-medium">{bilan.actif?.stocks?.total?.toLocaleString() || 0}€</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Créances clients</span>
                        <span className="font-medium">{bilan.actif?.creances?.total?.toLocaleString() || 0}€</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Trésorerie</span>
                        <span className="font-medium">{bilan.actif?.tresorerie?.total?.toLocaleString() || 0}€</span>
                      </div>
                      <div className="flex justify-between border-t pt-2 font-bold text-green-700 dark:text-green-400">
                        <span>TOTAL ACTIF</span>
                        <span>{bilan.actif?.total?.toLocaleString() || 0}€</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Passif */}
                  <div className="border rounded-lg p-4 bg-blue-50/30 dark:bg-blue-950/20">
                    <h4 className="font-semibold text-blue-700 dark:text-blue-400 mb-3">PASSIF</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>Capital</span>
                        <span className="font-medium">{bilan.passif?.capitaux_propres?.capital?.toLocaleString() || 0}€</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Réserves</span>
                        <span className="font-medium">{bilan.passif?.capitaux_propres?.reserves?.toLocaleString() || 0}€</span>
                      </div>
                      <div className="flex justify-between">
                        <span className={bilan.passif?.capitaux_propres?.resultat >= 0 ? 'text-green-600' : 'text-red-600'}>
                          Résultat de l'exercice
                        </span>
                        <span className={`font-medium ${bilan.passif?.capitaux_propres?.resultat >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {bilan.passif?.capitaux_propres?.resultat?.toLocaleString() || 0}€
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Emprunts</span>
                        <span className="font-medium">{bilan.passif?.dettes?.emprunts?.toLocaleString() || 0}€</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Dettes fournisseurs</span>
                        <span className="font-medium">{bilan.passif?.dettes?.fournisseurs?.toLocaleString() || 0}€</span>
                      </div>
                      <div className="flex justify-between border-t pt-2 font-bold text-blue-700 dark:text-blue-400">
                        <span>TOTAL PASSIF</span>
                        <span>{bilan.passif?.total?.toLocaleString() || 0}€</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Compte de Résultat */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5" />
                    Compte de Résultat - Exercice {exercice}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {compteResultat && (
                    <>
                      {/* Produits */}
                      <div className="border rounded-lg p-4 bg-green-50/30 dark:bg-green-950/20">
                        <h4 className="font-semibold text-green-700 dark:text-green-400 mb-3">PRODUITS</h4>
                        <div className="space-y-2 text-sm">
                          {Object.entries(compteResultat.produits?.ventes || {}).map(([cat, montant]) => (
                            <div key={cat} className="flex justify-between">
                              <span>{cat}</span>
                              <span className="font-medium">{montant?.toLocaleString() || 0}€</span>
                            </div>
                          ))}
                          <div className="flex justify-between">
                            <span>Subventions</span>
                            <span className="font-medium">{compteResultat.produits?.subventions?.toLocaleString() || 0}€</span>
                          </div>
                          <div className="flex justify-between border-t pt-2 font-bold text-green-700 dark:text-green-400">
                            <span>TOTAL PRODUITS</span>
                            <span>{compteResultat.produits?.total?.toLocaleString() || 0}€</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Charges */}
                      <div className="border rounded-lg p-4 bg-red-50/30 dark:bg-red-950/20">
                        <h4 className="font-semibold text-red-700 dark:text-red-400 mb-3">CHARGES</h4>
                        <div className="space-y-2 text-sm">
                          {Object.entries(compteResultat.charges?.achats || {}).map(([cat, montant]) => (
                            <div key={cat} className="flex justify-between">
                              <span>{cat}</span>
                              <span className="font-medium">{montant?.toLocaleString() || 0}€</span>
                            </div>
                          ))}
                          <div className="flex justify-between border-t pt-2 font-bold text-red-700 dark:text-red-400">
                            <span>TOTAL CHARGES</span>
                            <span>{compteResultat.charges?.total?.toLocaleString() || 0}€</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Résultat */}
                      <div className={`border rounded-lg p-4 ${compteResultat.resultat_net >= 0 ? 'bg-green-100 dark:bg-green-900/30' : 'bg-red-100 dark:bg-red-900/30'}`}>
                        <div className="flex justify-between items-center">
                          <span className="font-bold text-lg">RÉSULTAT NET</span>
                          <span className={`font-bold text-2xl ${compteResultat.resultat_net >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {compteResultat.resultat_net?.toLocaleString() || 0}€
                          </span>
                        </div>
                        {compteResultat.indicateurs && (
                          <p className="text-sm text-muted-foreground mt-2">
                            Marge: {compteResultat.indicateurs.taux_marge}% • CA mensuel moyen: {compteResultat.indicateurs.ca_mensuel?.toLocaleString()}€
                          </p>
                        )}
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <Scale className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <h3 className="font-medium mb-2">Aucune donnée de bilan</h3>
                <p className="text-sm text-muted-foreground">
                  Commencez par enregistrer des factures fournisseurs et clients
                </p>
              </CardContent>
            </Card>
          )}

          {/* Trésorerie */}
          {tresorerie && tresorerie.tresorerie?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Wallet className="w-5 h-5" />
                  Tableau de Trésorerie (12 derniers mois)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 px-3">Mois</th>
                        <th className="text-right py-2 px-3">Encaissements</th>
                        <th className="text-right py-2 px-3">Décaissements</th>
                        <th className="text-right py-2 px-3">Solde</th>
                        <th className="text-right py-2 px-3">Cumulé</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tresorerie.tresorerie.map((t, idx) => (
                        <tr key={idx} className="border-b hover:bg-muted/50">
                          <td className="py-2 px-3">{t.mois}</td>
                          <td className="text-right py-2 px-3 text-green-600">+{t.encaissements?.toLocaleString() || 0}€</td>
                          <td className="text-right py-2 px-3 text-red-600">-{t.decaissements?.toLocaleString() || 0}€</td>
                          <td className={`text-right py-2 px-3 font-medium ${t.solde >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {t.solde >= 0 ? '+' : ''}{t.solde?.toLocaleString() || 0}€
                          </td>
                          <td className={`text-right py-2 px-3 font-bold ${t.solde_cumule >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                            {t.solde_cumule >= 0 ? '+' : ''}{t.solde_cumule?.toLocaleString() || 0}€
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="font-bold bg-muted/30">
                        <td className="py-2 px-3">Solde final</td>
                        <td colSpan="3"></td>
                        <td className={`text-right py-2 px-3 ${tresorerie.solde_final >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                          {tresorerie.solde_final >= 0 ? '+' : ''}{tresorerie.solde_final?.toLocaleString() || 0}€
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Onglet Exports Comptables */}
        <TabsContent value="exports" className="mt-4 space-y-6">
          <div>
            <h3 className="font-semibold text-lg">Exports pour votre Cabinet Comptable</h3>
            <p className="text-sm text-muted-foreground">
              Générez les fichiers nécessaires pour votre expert-comptable
            </p>
          </div>

          <div className="flex items-center gap-4 mb-4">
            <Label className="text-sm">Exercice:</Label>
            <Select value={String(exercice)} onValueChange={(v) => setExercice(parseInt(v))}>
              <SelectTrigger className="w-[100px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[2024, 2025, 2026].map(year => (
                  <SelectItem key={year} value={String(year)}>{year}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            {/* Export FEC */}
            <Card className="hover:shadow-md transition-shadow">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <FileSpreadsheet className="w-5 h-5 text-blue-600" />
                  Export FEC
                </CardTitle>
                <CardDescription>
                  Fichier des Écritures Comptables - Format légal obligatoire
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-muted-foreground mb-4">
                  Format requis par l'administration fiscale. Contient toutes les écritures comptables de l'exercice.
                </p>
                <Button 
                  className="w-full gap-2" 
                  onClick={handleExportFEC}
                  disabled={loadingExport}
                >
                  {loadingExport ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                  Télécharger FEC
                </Button>
              </CardContent>
            </Card>

            {/* Export CSV */}
            <Card className="hover:shadow-md transition-shadow">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <FileText className="w-5 h-5 text-green-600" />
                  Export CSV
                </CardTitle>
                <CardDescription>
                  Données comptables au format tableur
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-muted-foreground mb-4">
                  Export compatible Excel/Google Sheets. Factures fournisseurs et clients séparées.
                </p>
                <Button 
                  variant="outline" 
                  className="w-full gap-2"
                  onClick={() => handleExportComptable('csv')}
                  disabled={loadingExport}
                >
                  {loadingExport ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                  Télécharger CSV
                </Button>
              </CardContent>
            </Card>

            {/* Export JSON */}
            <Card className="hover:shadow-md transition-shadow">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <FileText className="w-5 h-5 text-amber-600" />
                  Export JSON
                </CardTitle>
                <CardDescription>
                  Données structurées pour intégration
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-muted-foreground mb-4">
                  Format technique pour import dans un logiciel comptable ou API externe.
                </p>
                <Button 
                  variant="outline" 
                  className="w-full gap-2"
                  onClick={() => handleExportComptable('json')}
                  disabled={loadingExport}
                >
                  {loadingExport ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                  Télécharger JSON
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Info accès comptable */}
          <Card className="border-primary/30 bg-primary/5">
            <CardContent className="py-4">
              <div className="flex items-start gap-3">
                <Building2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium">Accès Cabinet Comptable</p>
                  <p className="text-sm text-muted-foreground">
                    Votre expert-comptable peut accéder directement à vos données via un portail dédié.
                    Contactez-nous pour configurer l'accès partagé.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modale aperçu facture fournisseur */}
      <InvoicePreviewDialog
        open={previewSupplier.open}
        onClose={() => setPreviewSupplier({ open: false, src: null, title: '' })}
        title={previewSupplier.title}
        mode={previewSupplier.mode || 'image'}
        src={previewSupplier.src}
      />
    </div>
  );
};

export default ComptabilitePage;
